import {
  ApiOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  EnvironmentOutlined,
  FileImageOutlined,
  FileTextOutlined,
  LineChartOutlined,
  PictureOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Layout, Menu as MenuAnt } from 'antd';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { SidebarMenu } from '../../constants/menu';
import { RootState } from '../../reducers';
import { Role } from '../../role/role';
import { $background, $lightgrey, $primary } from '../../styles/Variables';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const { Sider } = Layout;
const { SubMenu: SubMenuAnt } = MenuAnt;

const StyledSlider = styled(Sider)`
  background-color: #fff;

  .ant-layout-sider-children {
    display: grid;
    align-content: space-between;
    background-color: #fff;
    overflow: auto;
    /* width: 180px; */
  }
`;

const Menu = styled(MenuAnt)`
  list-style: none;
  padding: 0 20px 16px 20px;
  background-color: #fff;
  width: auto;

  &&.ant-menu {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-inline-end: none;
  }
`;

const MenuItem = styled(Link)<{ $active?: boolean }>`
  padding: 12px;
  cursor: pointer;
  border-radius: 12px;
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? 'var(--color-primary)' : 'var(--color-light-grey)'};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.05em;

  /* span.anticon {
    color: ${$lightgrey} !important;
  } */

  span {
    margin-right: 12px;
  }

  background-color: ${({ $active }) => ($active ? '#eff8ff' : 'transparent')};

  &:hover {
    background-color: #eff8ff;
  }

  /* .ant-menu-title-content,
  .ant-menu-title-content > a {
    font-weight: 600;
    color: ${({ $active }) =>
    $active ? 'var(--color-primary)' : 'var(--color-light-grey)'};
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.05em;
  } */

  &.active span {
    font-weight: 500;
  }

  &&.ant-menu-item-selected {
    background-color: transparent;

    .ant-menu-title-content {
      font-weight: 700;

      a {
        font-weight: inherit;
      }
    }

    ::after {
      content: none;
    }
  }
`;

const SubMenu = styled(SubMenuAnt)`
  .ant-menu-submenu-title {
    .ant-menu-title-content {
      font-weight: 700;
      font-size: 14px;
      line-height: 25px;
      letter-spacing: 0.01em;
      color: ${$primary};
    }
  }

  .ant-menu-sub {
    background: ${$background};
  }
`;

const Sidebar = () => {
  const { authUser, loading } = useSelector((state: RootState) => state.auth);
  const [activeMenu, setActiveMenu] = useState<SidebarMenu>();

  const onClickMenu = (menu: SidebarMenu) => {
    setActiveMenu(menu);
  };

  const renderAdminMenu = () => {
    return (
      <>
        <Menu
          className="h-60"
          defaultSelectedKeys={['videoSubMenu']}
          defaultOpenKeys={['recipeSubMenu', 'programSubMenu', 'videoSubMenu']}
          mode="inline"
          expandIcon={({ isOpen, isSubMenu }) =>
            isSubMenu && isOpen ? (
              <CaretUpOutlined size={12} />
            ) : (
              <CaretDownOutlined size={12} />
            )
          }
        >
          {authUser?.role === Role.ADMIN && (
            <>
              <MenuItem
                key="user"
                to="/user"
                // icon={<UserOutlined />}
                $active={activeMenu === SidebarMenu.USER}
                onClick={() => onClickMenu(SidebarMenu.USER)}
              >
                <UserOutlined />
                <span>User</span>
              </MenuItem>
              <MenuItem
                key="size"
                to="/size"
                // icon={<SettingOutlined />}
                $active={activeMenu === SidebarMenu.SIZE}
                onClick={() => onClickMenu(SidebarMenu.SIZE)}
              >
                <SettingOutlined />
                <span>Size</span>
              </MenuItem>
              <MenuItem
                key="image"
                to="/image"
                // icon={<FileImageOutlined />}
                $active={activeMenu === SidebarMenu.IMAGE}
                onClick={() => onClickMenu(SidebarMenu.IMAGE)}
              >
                <FileImageOutlined />
                <span>Image</span>
              </MenuItem>
            </>
          )}
          <MenuItem
            key="order"
            to="/order"
            // href="order"
            // icon={<ShoppingCartOutlined />}
            $active={activeMenu === SidebarMenu.ORDER}
            onClick={() => onClickMenu(SidebarMenu.ORDER)}
          >
            <ShoppingCartOutlined />
            <span>Order</span>
          </MenuItem>
          {authUser?.role === Role.ADMIN && (
            <MenuItem
              key="shippo-tracking"
              to="/shippo-tracking"
              $active={activeMenu === SidebarMenu.SHIPPO_TRACKING}
              onClick={() => onClickMenu(SidebarMenu.SHIPPO_TRACKING)}
            >
              <EnvironmentOutlined />
              <span>Shippo Tracking</span>
            </MenuItem>
          )}
          <MenuItem
            key="design"
            to="/design"
            // icon={<PictureOutlined />}
            $active={activeMenu === SidebarMenu.DESIGN}
            onClick={() => onClickMenu(SidebarMenu.DESIGN)}
          >
            <PictureOutlined />
            <span>Design</span>
          </MenuItem>
          {authUser?.role === Role.SELLER && (
            <MenuItem
              key="store"
              to="/stores"
              $active={activeMenu === SidebarMenu.STORE}
              onClick={() => onClickMenu(SidebarMenu.STORE)}
            >
              <ShopOutlined />
              <span>Stores</span>
            </MenuItem>
          )}
          <MenuItem
            key="invoice-info"
            to="/invoice-info"
            $active={activeMenu === SidebarMenu.INVOICE_INFO}
            onClick={() => onClickMenu(SidebarMenu.INVOICE_INFO)}
          >
            <FileTextOutlined />
            <span>Invoice Info</span>
          </MenuItem>
          <MenuItem
            key="stats"
            to="/stats"
            // icon={<LineChartOutlined />}
            $active={activeMenu === SidebarMenu.STATISTIC}
            onClick={() => onClickMenu(SidebarMenu.STATISTIC)}
          >
            <LineChartOutlined />
            <span>Statistics</span>
          </MenuItem>
          <MenuItem
            key="api-access"
            to="/api-access"
            $active={activeMenu === SidebarMenu.API_ACCESS}
            onClick={() => onClickMenu(SidebarMenu.API_ACCESS)}
          >
            <ApiOutlined />
            <span>Truy cập API</span>
          </MenuItem>
        </Menu>

        {/* <Menu>
          <MenuItem key="settingsTab">
            <Link to="/setting">Setting</Link>
          </MenuItem>
        </Menu> */}
      </>
    );
  };

  const renderMasterAdminMenu = () => {
    return (
      <>
        <Menu
          className="h-60"
          defaultSelectedKeys={['adminAccountsTab']}
          mode="inline"
          expandIcon={({ isOpen, isSubMenu }) =>
            isSubMenu && isOpen ? (
              <CaretUpOutlined size={12} />
            ) : (
              <CaretDownOutlined size={12} />
            )
          }
        >
          <MenuItem key="adminAccountsTab" to="/admins">
            <span>Admin Accounts</span>
          </MenuItem>
        </Menu>

        <Menu>
          <MenuItem key="settingsTab" to="/setting">
            <span>Setting</span>
          </MenuItem>
        </Menu>
      </>
    );
  };

  return (
    <StyledSlider width={290}>
      {loading && <LoadingSpinner />}
      {/* {authUser?.role === Role.ADMIN && renderAdminMenu()} */}
      {true && renderAdminMenu()}
      {authUser?.role === Role.MASTER_ADMIN && renderMasterAdminMenu()}
    </StyledSlider>
  );
};
export default Sidebar;
